import { useEffect, useState } from "react"

/**
 * A React hook that detects when an element is visible in the viewport using the Intersection Observer API.
 *
 * @param {React.RefObject} ref - A ref to the element to observe
 * @param {string} rootMargin - The root margin for the intersection observer (default: "0px")
 * @returns {boolean} True if the element is intersecting with the viewport, false otherwise
 *
 * @example
 * ```jsx
 * function LazyLoadComponent() {
 *   const ref = useRef();
 *   const isOnScreen = useOnScreen(ref);
 *
 *   return (
 *     <div ref={ref}>
 *       {isOnScreen ? <ExpensiveComponent /> : <div>Loading...</div>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useOnScreen(ref, rootMargin = "0px") {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting)
      },
      {
        rootMargin
      }
    )
    if (node) {
      observer.observe(node)
    }
    return () => {
      if (node) {
        observer.unobserve(node)
      }
    }
  }, [ref, rootMargin])

  return isIntersecting
}
